import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { registerLocaleData } from '@angular/common';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import localeDe from '@angular/common/locales/de';
import localeFr from '@angular/common/locales/fr';
import localeIt from '@angular/common/locales/it';
import {
  EnvironmentInjector,
  createEnvironmentInjector,
  importProvidersFrom,
  inject,
  isDevMode,
  provideAppInitializer,
  provideZonelessChangeDetection,
} from '@angular/core';
import { MatDateFnsModule } from '@angular/material-date-fns-adapter';
import { DateAdapter } from '@angular/material/core';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { bootstrapApplication } from '@angular/platform-browser';
import { Router, provideRouter, withComponentInputBinding, withInMemoryScrolling, withRouterConfig } from '@angular/router';
import { JWT_OPTIONS, JwtHelperService } from '@auth0/angular-jwt';
import { provideEffects } from '@ngrx/effects';
import { provideState, provideStore } from '@ngrx/store';
import { NgxPermissionsModule } from 'ngx-permissions';
import { progressInterceptor } from 'ngx-progressbar/http';
import { config as configureZod } from 'zod';

import { AppComponent } from './app/app.component';
import { reducers } from './app/app.reducers';
import { authReducers } from './app/auth/store';
import { AuthEffects } from './app/auth/store/auth/auth.effects';
import { SNACK_BAR_DURATION } from './app/core/constants';
import { CAPTURED_CONSOLE_LEVELS } from './app/core/errors/lari-error-handler';
import { provideErrorBoundary } from './app/core/errors/provide-error-boundary';
import { httpErrorInterceptor } from './app/core/interceptors/http-error.interceptor';
import { jwtInterceptor } from './app/core/interceptors/jwt.interceptor';
import { nonAuthInterceptor } from './app/core/interceptors/non-auth.interceptor';
import { trailingSlashesInterceptor } from './app/core/interceptors/trailing-slashes.interceptor';
import { AppInitService } from './app/core/services/app-init/app-init.service';
import { UtilsService } from './app/core/services/helpers/utils.service';
import { coreReducers, paginationReducers } from './app/core/store';
import { PaginationEffects } from './app/core/store/pagination/pagination.effects';
import { TranslationEffects } from './app/core/store/translations/translations.effects';
import { CustomFnsDateAdapter } from './app/core/utils/custom-fns-date-adapter.util';
import { jwtOptionsFactory } from './app/core/utils/jwt-options-factory.util';
import { ngxTranslationInitializerFactory } from './app/core/utils/ngx-translation-initializer-factory.util';
import { warnOnSupersededQueryWrites } from './app/core/utils/route-params.codec';
import { TableAttributesEffects } from './app/project/modules/tables/store/table-attributes.effects';
import { projectEffects } from './app/project/projects.effects';
import { projectReducers } from './app/project/store';
import { routes } from './app/routes';
import { preventUnauthRequestsInterceptor } from './app/shared/interceptors/prevent-unauth-requests.interceptor';
import { recentlyViewedInterceptor } from './app/shared/interceptors/recently-viewed.interceptor';
import { waitAllRequestsInterceptor } from './app/shared/interceptors/wait-all-requests.interceptor';
import { ExternalSharingEffects } from './app/shared/modules/external-sharing/store/external-sharing.effects';
import { externalSharingReducer } from './app/shared/modules/external-sharing/store/external-sharing.reducer';
import { FilteredTableEffects } from './app/shared/modules/filtered-table/store/filtered-table.effects';
import { reducer } from './app/shared/modules/filtered-table/store/filtered-table.reducer';
import { pageReadOnlyReducer } from './app/shared/modules/read-only/store/read-only.reducer';
import { ticketingUserInterceptor } from './app/ticketing/interceptors/ticketing-user.interceptor';
import { patchUserCountsInterceptor } from './app/user/interceptors/patch-user-counts.interceptor';
import { userReducers } from './app/user/store';
import { UserInfoEffects } from './app/user/store/user-info/user-info.effects';
import { UserSettingsEffects } from './app/user/store/user-settings/user-settings.effects';
import { policy, release, sentryDsn, tier } from './app/core/tier';
import { storeDevtools } from './environments/store-devtools';
import type { ManageTableDialogLoader } from './app/shared/modules/editors/tokens/manage-table-dialog.token';
import { MANAGE_TABLE_DIALOG_LOADER } from './app/shared/modules/editors/tokens/manage-table-dialog.token';

registerLocaleData(localeFr);
registerLocaleData(localeIt);
registerLocaleData(localeDe);

// Disable Zod v4's JIT validator (which compiles schemas via `new Function`).
// Our CSP has no `unsafe-eval`, so the JIT capability probe throws and Firefox
// logs a CSP violation; Zod already falls back to its interpreter, so opting out
// up front just skips the doomed probe. See zod's `globalConfig.jitless`.
configureZod({ jitless: true });
export const initializeApp = () => {
  const appInitService: AppInitService = inject(AppInitService);
  return appInitService.init();
};

if (sentryDsn !== undefined) {
  void import('@sentry/angular').then(sentry => {
    sentry.init({
      dsn: sentryDsn,
      environment: tier,
      release,
      // Angular's own global listeners already route window errors to LariErrorHandler; leaving
      // GlobalHandlers on means dedupe drops our (higher-fidelity) event and `lastEventId` lies.
      integrations: defaults => [
        ...defaults.filter(integration => integration.name !== 'GlobalHandlers'),
        sentry.browserTracingIntegration(),
        sentry.captureConsoleIntegration({ levels: CAPTURED_CONSOLE_LEVELS }),
      ],
      tracePropagationTargets: ['localhost', /https:\/\/.*\.lari\.(digital|systems)\/api/],
      sampleRate: policy.sampleRate,
      tracesSampleRate: policy.tracesSampleRate,
    });
  });
}

bootstrapApplication(AppComponent, {
  providers: [
    provideZonelessChangeDetection(),
    ...provideErrorBoundary(),
    provideStore(reducers, {
      runtimeChecks: {
        strictStateImmutability: policy.isInternal,
        strictActionImmutability: policy.isInternal,
      },
    }),
    provideState('user', userReducers),
    provideState('auth', authReducers),
    provideState('core', coreReducers),
    provideState('pagination', paginationReducers),
    provideState('externalSharing', externalSharingReducer),
    provideState('projects', projectReducers),
    provideState('readOnly', pageReadOnlyReducer),
    provideState('filteredTable', reducer),
    provideEffects(
      ExternalSharingEffects,
      UserInfoEffects,
      UserSettingsEffects,
      AuthEffects,
      TranslationEffects,
      PaginationEffects,
      FilteredTableEffects,
      ...projectEffects,
      TableAttributesEffects,
    ),
    ...(policy.isInternal ? storeDevtools : []),
    provideRouter(
      routes,
      withRouterConfig({ paramsInheritanceStrategy: 'always' }),
      withInMemoryScrolling({ anchorScrolling: 'enabled' }),
      withComponentInputBinding({ unmatchedInputBehavior: 'undefinedIfStale' }),
    ),
    { provide: JWT_OPTIONS, useFactory: jwtOptionsFactory, deps: [UtilsService] },
    JwtHelperService,
    importProvidersFrom(NgxPermissionsModule.forRoot(), MatDateFnsModule),
    { provide: DateAdapter, useClass: CustomFnsDateAdapter },
    { provide: MAT_SNACK_BAR_DEFAULT_OPTIONS, useValue: { duration: SNACK_BAR_DURATION } },
    {
      provide: OVERLAY_DEFAULT_CONFIG,
      useValue: {
        usePopover: false,
      },
    },
    {
      provide: MANAGE_TABLE_DIALOG_LOADER,
      useFactory: (): ManageTableDialogLoader => {
        const parentInjector = inject(EnvironmentInjector);
        // The dialog opens from any editor, while TableEffects is otherwise provided only by the
        // issue and table routes. The injector keeps that registration for the session.
        let tableEffectsInjector: EnvironmentInjector | undefined;
        return async () => {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          const [{ ManageFilteredTableDialogComponent }, { TableEffects }] = await Promise.all([
            import('./app/shared/modules/common-table/manage-filtered-table-dialog/manage-filtered-table-dialog.component'),
            import('./app/project/modules/tables/store/table.effects'),
          ]);
          tableEffectsInjector ??= createEnvironmentInjector([provideEffects(TableEffects)], parentInjector);
          return ManageFilteredTableDialogComponent;
        };
      },
    },
    provideAppInitializer(ngxTranslationInitializerFactory),
    provideAppInitializer(initializeApp),
    provideAppInitializer(() => {
      if (isDevMode()) {
        warnOnSupersededQueryWrites(inject(Router));
      }
    }),
    provideHttpClient(
      withInterceptors([
        jwtInterceptor,
        trailingSlashesInterceptor,
        httpErrorInterceptor,
        nonAuthInterceptor,
        waitAllRequestsInterceptor,
        patchUserCountsInterceptor,
        ticketingUserInterceptor,
        preventUnauthRequestsInterceptor,
        recentlyViewedInterceptor,
        progressInterceptor,
      ]),
      withFetch(),
    ),
  ],
})
  // eslint-disable-next-line no-console
  .catch((err: unknown) => console.log(err));
